import { getPositionWithFallback, useLocationPermissionSync } from "../../../../utils/getPositionWithFallback";
import React, { useState, useEffect, useRef } from "react";
import { Box, IconButton, useTheme, useMediaQuery, Paper, List, ListItem, ListItemText } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RoomIcon from "@mui/icons-material/Room";
import { GoogleMap, Marker, Circle, useJsApiLoader } from "@react-google-maps/api";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

import LocationPermissionModal from "../../../../components/PermissionModal";
import CustomText, { textStyles } from "../../../../components/CustomText";
import CustomButton from "../../../../components/CustomButton";
import crossmap from "../../../../assets/crossmap.svg";

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const TaskLocation = ({ formValues, setFormValues, radius, onPermission }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState("No address found");
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showFullMap, setShowFullMap] = useState(false);
  const [mapRef, setMapRef] = useState(null);
  const [hasCheckedPermission, setHasCheckedPermission] = useState(false);

  const addressRef = useRef(null);

  // Helper function to check internet connectivity
  const isOnline = () => navigator.onLine;

  // Enable the close ("X") button on the native Street View panorama that
  // opens when the map's built-in pegman is dragged onto the map, so users
  // can get back to the map view (it's off by default in the JS API), and
  // force a resize once visible since the panorama can otherwise render
  // black inside a React-managed container.
  const setupStreetView = (map) => {
    const panorama = map.getStreetView();
    panorama.setOptions({
      enableCloseButton: true,
      // Restrict the pegman to official outdoor Street View coverage only.
      // Without this it can snap to low-quality/broken user- or business-
      // contributed 360 photos (indoor photospheres), which sometimes render
      // solid black.
      source: window.google.maps.StreetViewSource.OUTDOOR,
    });
    const triggerResize = () => {
      requestAnimationFrame(() => {
        window.google.maps.event.trigger(panorama, "resize");
      });
      setTimeout(() => {
        window.google.maps.event.trigger(panorama, "resize");
      }, 300);
    };

    panorama.addListener("visible_changed", () => {
      if (panorama.getVisible()) triggerResize();
    });
    panorama.addListener("pano_changed", () => {
      if (panorama.getVisible()) triggerResize();
    });
  };

  // Check permission on component mount
  useEffect(() => {
    if (!hasCheckedPermission) {
      setHasCheckedPermission(true);
      console.log("🚀 TaskLocation mounted - Checking permission...");

      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          if (result.state === 'denied') {
            console.log("❌ Permission denied");
            setShowPermissionModal(true);
            onPermission?.(false);
          } else if (result.state === 'granted') {
            console.log("✅ Permission granted");
            handleGetCurrentLocation();
          } else {
            // 'prompt' state - will ask when getCurrentLocation is called
            console.log("❓ Permission prompt");
            handleGetCurrentLocation();
          }
        });
      } else {
        // Fallback for browsers that don't support permissions API
        handleGetCurrentLocation();
      }
    }
  }, [hasCheckedPermission]);

  // Reverse geocode using Google Maps Geocoding API
  const fetchAddress = async ({ lat, lng }) => {
    if (!window.google) {
      console.log("⚠️ Google Maps not loaded");
      return;
    }

    // Check internet before fetching
    if (!isOnline()) {
      console.error("❌ No internet connection");
      toast.error(t("locationInternetError") || "No internet connection");
      setAddress("No internet connection");
      return;
    }

    console.log("🗺️ Fetching address for:", { lat, lng });
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      console.log("🗺️ Geocoding status:", status);
      console.log("🗺️ Geocoding results:", results);

      if (status === "OK" && results[0]) {
        const formattedAddress = results[0].formatted_address;
        const addressComponents = results[0].address_components;

        // Extract address components
        let country = "";
        let province = "";
        let city = "";
        let community = "";
        let postalCode = "";
        let streetAddress = "";

        addressComponents.forEach((component) => {
          const types = component.types;

          if (types.includes("country")) {
            country = component.long_name;
          }
          if (types.includes("administrative_area_level_1")) {
            province = component.long_name;
          }
          if (types.includes("locality") || types.includes("postal_town")) {
            city = component.long_name;
          }
          if (types.includes("sublocality") || types.includes("neighborhood")) {
            community = component.long_name;
          }
          if (types.includes("postal_code")) {
            postalCode = component.long_name;
          }
          if (types.includes("route")) {
            streetAddress = component.long_name;
          }
          if (types.includes("street_number")) {
            streetAddress = component.long_name + " " + streetAddress;
          }
        });

        // If city is not found, try alternative
        if (!city) {
          addressComponents.forEach((component) => {
            if (component.types.includes("administrative_area_level_2")) {
              city = component.long_name;
            }
          });
        }

        const structured = {
          lat,
          lng,
          address: formattedAddress,
          streetAddress: streetAddress || "",
          city: city || "",
          province: province || "",
          community: community || "",
          postalCode: postalCode || "",
          country: country || "",
        };

        console.log("✅ Structured address:", structured);

        setAddress(formattedAddress);
        if (addressRef.current) {
          addressRef.current.value = formattedAddress;
        }

        setFormValues((prev) => ({
          ...prev,
          location: structured,
        }));
      } else {
        if (!isOnline()) {
          console.error("❌ No internet connection");
          setAddress("No internet connection");
        } else {
          console.error("❌ Geocoding failed:", status);
          setAddress("No address found");
        }
        
        setFormValues((prev) => ({
          ...prev,
          location: {
            lat,
            lng,
            address: "No address found",
          },
        }));
      }
    });
  };

  // Handle search query change for autocomplete
  useEffect(() => {
    if (!searchQuery.trim() || !window.google) {
      setSuggestions([]);
      return;
    }

    // Check internet before searching
    if (!isOnline()) {
      console.log("⚠️ No internet - skipping search");
      setSuggestions([]);
      return;
    }

    console.log("🔍 Searching for:", searchQuery);

    const service = new window.google.maps.places.AutocompleteService();

    const request = {
      input: searchQuery,
      types: ["geocode", "establishment"],
    };

    service.getPlacePredictions(request, (predictions, status) => {
      console.log("🔍 Predictions status:", status);
      console.log("🔍 Predictions:", predictions);

      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setSuggestions(predictions);
      } else {
        setSuggestions([]);
      }
    });
  }, [searchQuery]);

  // Handle search suggestion selection
  const handleSearchSelect = (suggestion) => {
    if (!window.google) return;

    // Check internet before searching
    if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection");
      return;
    }

    console.log("📍 Selected suggestion:", suggestion);
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
      console.log("📍 Place geocoding status:", status);

      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;

        const coords = {
          lat: location.lat(),
          lng: location.lng(),
        };

        console.log("✅ Selected location:", coords);

        setPosition(coords);
        fetchAddress(coords);
        setSearchQuery("");
        setSuggestions([]);

        // Pan map to new location
        if (mapRef) {
          mapRef.panTo(coords);
          mapRef.setZoom(15);
        }
      }
    });
  };

  // Handle map click
  const handleMapClick = (e) => {
    const coords = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    console.log("🖱️ Map clicked at:", coords);
    setPosition(coords);
    fetchAddress(coords);
  };

  // Handle marker drag
  const handleMarkerDragEnd = (e) => {
    const coords = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    console.log("🔄 Marker dragged to:", coords);
    setPosition(coords);
    fetchAddress(coords);
  };

  // Handle current location button with permission check and internet check
  const handleGetCurrentLocation = () => {
    console.log("📍 Requesting current location...");

    if (!navigator.geolocation) {
      console.error("❌ Geolocation not supported");
      toast.error(t("geolocationNotSupported") || "Geolocation not supported");
      setShowPermissionModal(true);
      onPermission?.(false);
      return;
    }

    // Check internet connectivity
    if (!isOnline()) {
      console.error("❌ No internet connection");
      toast.error(t("locationInternetError") || "No internet connection. Please check your connection.");
      setShowPermissionModal(true);
      return;
    }

    getPositionWithFallback(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        console.log("✅ Current location:", coords);
        setPosition(coords);
        fetchAddress(coords);
        onPermission?.(true);
        setShowPermissionModal(false); // ✅ Hide modal on success

        // Pan map to current location
        if (mapRef) {
          mapRef.panTo(coords);
          mapRef.setZoom(15);
        }
      },
      (error) => {
        console.error("❌ Location error:", error);

        if (error.code === error.PERMISSION_DENIED) {
          console.log("❌ Permission denied by user");
          setShowPermissionModal(true);
        } else if (error.code === error.TIMEOUT) {
          toast.error(t("locationTimeout") || "Location request timed out");
        } else {
          toast.error(t("unableToFetchLocation") || "Unable to fetch location");
        }

        onPermission?.(false);
      }
    );
  };

  // Online/Offline event listeners

  // Reflect browser location allow/block changes live (no refresh needed).
  useLocationPermissionSync({
    onGranted: () => {
      onPermission?.(true);
      if (showPermissionModal) {
        setShowPermissionModal(false);
        handleGetCurrentLocation();
      }
    },
    onDenied: () => {
      setShowPermissionModal(true);
      onPermission?.(false);
    },
  });
  useEffect(() => {
    const handleOnline = () => {
      toast.success(t("internetRestored") || "Internet connection restored");
      // Retry fetching address if coordinates exist
      if (position?.lat && position?.lng) {
        fetchAddress(position);
      }
    };

    const handleOffline = () => {
      toast.error(t("internetConnectionLost") || "Internet connection lost. Please check your network.");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [position]);

  return (
    <Box>
      {/* Header actions */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <CustomText customStyle={{ ...textStyles.body4, color: theme.palette.text.primary }}>
          {t("pickFromLive")} <span style={{ color: "red" }}>*</span>
        </CustomText>
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* Search toggle */}
          <IconButton
            onClick={() => {
              const newState = !searchBarOpen;
              console.log("🔍 Search toggled:", newState);
              setSearchBarOpen(newState);
              if (!newState) {
                setSearchQuery("");
                setSuggestions([]);
              }
            }}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              borderRadius: 1,
              width: 40,
              height: 31,
              "&:hover": { backgroundColor: "#1976d2" },
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>

          {/* Current location */}
          <IconButton
            onClick={handleGetCurrentLocation}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              borderRadius: 1,
              width: 40,
              height: 31,
              "&:hover": { backgroundColor: "#1976d2" },
            }}
          >
            <RoomIcon fontSize="small" />
          </IconButton>

          {/* Full Map button */}
          <CustomButton
            label={t("fullMap")}
            size="small"
            sx={{
              height: 31,
              px: 2,
              backgroundColor: "#1976d2",
              color: "#fff",
              fontSize: 12,
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={() => {
              console.log("🗺️ Opening full map");
              setShowFullMap(true);
            }}
          />
        </Box>
      </Box>

      {/* Search bar with suggestions */}
      {searchBarOpen && (
        <Box sx={{ mb: 2, position: "relative" }}>
          <input
            type="text"
            placeholder={t("searchLocation")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 14px",
              fontSize: "15px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              fontFamily: "Poppins, sans-serif",
            }}
          />

          {/* Suggestions dropdown */}
          {suggestions.length > 0 && (
            <Paper
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                maxHeight: "200px",
                overflowY: "auto",
                zIndex: 10000,
                mt: 0.5,
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              }}
            >
              <List>
                {suggestions.map((sug) => (
                  <ListItem
                    key={sug.place_id}
                    button
                    onClick={() => handleSearchSelect(sug)}
                    sx={{
                      "&:hover": { backgroundColor: "#f0f0f0" },
                      cursor: "pointer",
                    }}
                  >
                    <ListItemText
                      primary={sug.structured_formatting.main_text}
                      secondary={sug.structured_formatting.secondary_text}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontFamily: "Poppins, sans-serif",
                      }}
                      secondaryTypographyProps={{
                        fontSize: "12px",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      )}

      {/* Map preview */}
      <Box sx={{ height: 300, borderRadius: 2, overflow: "hidden" }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={position || { lat: 0, lng: 0 }}
          zoom={15}
          onClick={handleMapClick}
          onLoad={(map) => {
            console.log("🗺️ Map loaded (preview)");
            setMapRef(map);
            setupStreetView(map);
          }}
          options={{
            zoomControl: true,
            streetViewControl: true,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {position && (
            <>
              <Marker
                position={position}
                draggable={true}
                onDragEnd={handleMarkerDragEnd}
              />
              <Circle
                center={position}
                radius={Number(radius) || 100}
                options={{
                  fillColor: "#1976d2",
                  fillOpacity: 0.2,
                  strokeColor: "#1976d2",
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                }}
              />
            </>
          )}
        </GoogleMap>
      </Box>

      {/* Address field */}
      <textarea
        ref={addressRef}
        readOnly
        value={address}
        rows={3}
        style={{
          width: "100%",
          padding: "12px",
          fontSize: "16px",
          border: "1px solid #ccc",
          borderRadius: "8px",
          marginTop: "10px",
          backgroundColor: "#f4f4f4",
          fontFamily: "Poppins, sans-serif",
          resize: "vertical",
          overflowY: "auto",
          lineHeight: "1.4",
        }}
      />

      {/* Full Map overlay */}
      {showFullMap && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "#fff",
            zIndex: 1300,
          }}
        >
          <IconButton
            onClick={() => {
              console.log("🗺️ Closing full map");
              setShowFullMap(false);
            }}
            sx={{ position: "absolute", top: 16, right: 16, zIndex: 1400 }}
          >
            <img src={crossmap} alt="close" style={{ width: 50, height: 50 }} />
          </IconButton>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={position || { lat: 0, lng: 0 }}
            zoom={15}
            onClick={handleMapClick}
            onLoad={(map) => {
              console.log("🗺️ Full map loaded");
              setupStreetView(map);
            }}
            options={{
              zoomControl: true,
              streetViewControl: true,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
          >
            {position && (
              <>
                <Marker
                  position={position}
                  draggable={true}
                  onDragEnd={handleMarkerDragEnd}
                />
                <Circle
                  center={position}
                  radius={Number(radius) || 100}
                  options={{
                    fillColor: "#1976d2",
                    fillOpacity: 0.2,
                    strokeColor: "#1976d2",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                  }}
                />
              </>
            )}
          </GoogleMap>
        </Box>
      )}

      {/* Permission Modal */}
      <LocationPermissionModal
        open={showPermissionModal}
        onAllow={() => {
          console.log("✅ Location permission allowed");
          setShowPermissionModal(false);
          handleGetCurrentLocation(); // ✅ Retry after user allows
        }}
        onCancel={() => {
          console.log("❌ Location permission denied");
          setShowPermissionModal(false);
          onPermission?.(false);
        }}
      />
    </Box>
  );
};

export default TaskLocation;